/**
 * Every byte through fetch (mission 6 T013; FR-017, FR-018, FR-026, C-005;
 * SEC.7, D.1; research §1.14; post-plan C4, S7, S9, T26; post-tasks E5).
 *
 * The only module besides `lib/api.ts` that calls fetch, and the only one that
 * touches a presigned URL.
 *
 * - API URLs come from `apiUrl()` and carry `credentials: 'include'`; presigned
 *   object-store URLs carry `credentials: 'omit'` and `cache: 'no-store'` (S9).
 * - A presigned URL lives in a local variable, is fetched at once, and never
 *   reaches state, the DOM, a return value, a log or an Error (S9). Errors are
 *   `TransferError`s whose message is a register sentence, or the API's
 *   `ApiError`, whose message is the problem title.
 * - `report_url` is resolved against the API origin, never the page origin, and
 *   refused unfetched unless it lands there (C4, S7).
 * - A body is saved only when it arrived whole: a size mismatch, a failed read
 *   or an abort saves nothing (register 12), and an export or report that is
 *   not `text/csv` is refused (S7).
 * - Saving is one detached `<a download>` on a `blob:` URL, revoked on the next
 *   task. No link to an API route, no `window.open`, no navigation (FR-026).
 * - The upload grant, the PUT and the completion run in one function whose
 *   result is the completion body alone, so the grant URL never enters a
 *   TanStack cache (S9).
 *
 * Frozen after mission 6 WP02 (T17). Mission 9 WP11 (T070; FR-012, OR-14, U7;
 * a declared edit) adds `downloadStatement`. Mission 10 WP15 (T085; FR-016, FR-018;
 * OR-14) adds `exportAuditLog`.
 */
import { api, apiOrigin, apiUrl, idSegment, oneOf, refusal } from './api'
import { auditSearch, type AuditFilters } from './api/audit'
import { statementFile } from './api/billing'
import type { RunPath } from './api/runs'
import { completeUpload, grantUpload, type Upload } from './api/uploads'
import { shortId } from './format'
import { copyText } from './provisional-copy'

export type TransferErrorKind = 'incomplete' | 'not-csv' | 'off-origin' | 'expired' | 'storage'

const TRANSFER_COPY = {
  incomplete: 'downloadIncomplete',
  'not-csv': 'genericError',
  'off-origin': 'genericError',
  expired: 'downloadIncomplete',
  storage: 'storageFailed',
} as const

/** A transfer that did not complete. The message is a register sentence, never a URL or a status line. */
export class TransferError extends Error {
  constructor(readonly kind: TransferErrorKind) {
    super(copyText(TRANSFER_COPY[kind]))
    this.name = 'TransferError'
  }
}

export type DownloadObject = 'generated' | 'upload'
const DOWNLOAD_OBJECTS: readonly DownloadObject[] = ['generated', 'upload']

interface Issuance {
  url: string
  filename: string
  sha256: string | null
  size: number
}

export interface Downloaded {
  filename: string
  sha256: string | null
  size: number
}

export interface Saved {
  filename: string
  bytes: number
}

const SAFE_NAME = /^[A-Za-z0-9._-]{1,128}$/

/** Save a Blob: one detached `<a download>` on a `blob:` URL, revoked on the next task. */
export function saveBlob(blob: Blob, filename: string): void {
  const href = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  a.click()
  setTimeout(() => URL.revokeObjectURL(href), 0)
}

/**
 * GET /v1/runs/{id}/download?object=… then the presigned URL, at once (D.1, A.5).
 * An expired or refused URL is `expired`: "Download again" is a new issuance.
 */
export async function downloadObject(runId: string, object: DownloadObject): Promise<Downloaded> {
  const path = `/runs/${idSegment(runId, 'run id')}/download?object=${oneOf(object, DOWNLOAD_OBJECTS, 'object')}`
  const issued = await api<Issuance>(path)
  const url = issued.url
  let res: Response
  try {
    res = await fetch(url, { credentials: 'omit', cache: 'no-store' })
  } catch {
    throw new TransferError('expired')
  }
  if (!res.ok) throw new TransferError('expired')
  let blob: Blob
  try {
    blob = await res.blob()
  } catch {
    throw new TransferError('incomplete')
  }
  if (blob.size !== issued.size) throw new TransferError('incomplete')
  saveBlob(blob, issued.filename)
  return {
    filename: issued.filename,
    sha256: issued.sha256 === null ? null : issued.sha256.toLowerCase(),
    size: issued.size,
  }
}

function isCsv(res: Response): boolean {
  const type = res.headers.get('Content-Type') ?? ''
  return type.split(';')[0]?.trim().toLowerCase() === 'text/csv'
}

function dispositionName(res: Response, fallback: string): string {
  const match = /filename="([^"]*)"/.exec(res.headers.get('Content-Disposition') ?? '')
  const name = match?.[1]
  return name !== undefined && SAFE_NAME.test(name) ? name : fallback
}

interface ReadOptions {
  onBytes?: (total: number) => void
  signal?: AbortSignal
}

/** Fetch an API CSV with include, read it whole, and save it (FR-017, ANA.12). */
async function fetchCsv(url: string, fallbackName: string, options: ReadOptions): Promise<Saved> {
  let res: Response
  try {
    res = await fetch(url, options.signal ? { credentials: 'include', signal: options.signal } : { credentials: 'include' })
  } catch {
    throw new TransferError('incomplete')
  }
  if (!res.ok) throw await refusal(res)
  if (!isCsv(res) || res.body === null) throw new TransferError('not-csv')

  const chunks: Uint8Array[] = []
  let total = 0
  try {
    const reader = res.body.getReader()
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      chunks.push(value)
      total += value.byteLength
      options.onBytes?.(total)
    }
  } catch {
    throw new TransferError('incomplete')
  }
  const filename = dispositionName(res, fallbackName)
  saveBlob(new Blob(chunks as BlobPart[], { type: 'text/csv' }), filename)
  return { filename, bytes: total }
}

/**
 * GET /v1/runs/{id}/findings/export: the complete finding list (FR-017;
 * DM-01M39TVGN8PD0659S6ZFE3P726). The name is Content-Disposition's when it is a
 * plain file name, else `trellis-run-<first 8>-findings.csv` (T26).
 */
export async function exportFindings(runId: string, options: ReadOptions = {}): Promise<Saved> {
  const url = apiUrl(`/runs/${idSegment(runId, 'run id')}/findings/export`)
  return fetchCsv(url, `trellis-run-${shortId(runId)}-findings.csv`, options)
}

/** The validation report behind a rejected upload's `report_url` (U.2, D.1). */
export async function validationReport(reportUrl: string, runId: string): Promise<Saved> {
  idSegment(runId, 'run id')
  const fallback = `validation-report-${runId}.csv`
  const origin = apiOrigin()
  let resolved: URL
  try {
    resolved = new URL(reportUrl, origin)
  } catch {
    throw new TransferError('off-origin')
  }
  if (resolved.origin !== origin) throw new TransferError('off-origin')
  return fetchCsv(resolved.href, fallback, {})
}

/**
 * Grant, PUT and completion in one function (P.3, U.1, U.4; S9). The grant's
 * `content_type` is the caller's `contentType`, never `file.type`: a Path B
 * `.dat` has an empty `file.type` and must be granted as
 * `application/octet-stream` (E5). Problems from the grant and the completion
 * are rethrown as the ApiError they are; a failed PUT is `storage` (register 16).
 */
export async function uploadFile(file: File, path: RunPath, contentType: string): Promise<Upload> {
  const grant = await grantUpload({ path, filename: file.name, content_type: contentType, size_bytes: file.size })
  const url = grant.url
  let res: Response
  try {
    res = await fetch(url, { method: grant.method, body: file, headers: grant.headers, credentials: 'omit' })
  } catch {
    throw new TransferError('storage')
  }
  if (!res.ok) throw new TransferError('storage')
  return completeUpload(grant.upload_id)
}

/**
 * GET /v1/billing/statements/{file}: a cycle's usage statement (FR-012, B.7; OR-14, U7), fetched with
 * credentials, saved only when it is a complete `text/csv` body, as `trellis-usage-<file>`. Never a
 * link to the API route, a `window.open` or a navigation (mission 6 FR-026). A cycle start that is not
 * a timestamp is an `InvalidIdError` before any fetch.
 */
export async function downloadStatement(cycleStart: string): Promise<Saved> {
  const file = statementFile(cycleStart)
  return fetchCsv(apiUrl(`/billing/statements/${file}`), `trellis-usage-${file}`, {})
}

/**
 * GET /v1/audit/export: the audit log under the sheet 44 filters as CSV (FR-016, FR-018, J.6; mission 9
 * OR-14), fetched with credentials and saved only when it is a complete `text/csv` body, under the server's
 * `trellis-audit-<YYYYMMDD>.csv` (or `trellis-audit.csv` when the header names nothing usable). Never an
 * `<a href>` to the route, a `window.open` or a navigation: the route appends a `download` event and is
 * guarded against cross-site requests (OR-6). The filters are `listAudit`'s, validated before any fetch.
 */
export async function exportAuditLog(filters: AuditFilters, options: ReadOptions = {}): Promise<Saved> {
  const search = auditSearch(filters).toString()
  return fetchCsv(apiUrl(`/audit/export${search === '' ? '' : `?${search}`}`), 'trellis-audit.csv', options)
}
